import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Volume2, Moon, Sun, BookOpen, Lightbulb, ArrowLeft, Brain, Settings, Play, Star, GraduationCap } from 'lucide-react';
import './App.css';

// Get part of speech color
const getPartOfSpeechColor = (pos) => {
  const colors = {
    noun: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    verb: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    adjective: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    adverb: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    default: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  };
  return colors[pos.toLowerCase()] || colors.default;
};

// Animated Star Rating Component
const AnimatedStars = ({ score, totalQuestions }) => {
  const [visibleStars, setVisibleStars] = useState(0);
  const percentage = Math.round((score / totalQuestions) * 100);
  
  // Determine number of stars based on percentage
  let starCount = 0;
  if (percentage === 100) starCount = 3;
  else if (percentage >= 51) starCount = 2;
  else if (percentage > 0) starCount = 1;
  
  useEffect(() => {
    // Animate stars appearing one by one
    const timer = setTimeout(() => {
      if (visibleStars < starCount) {
        setVisibleStars(prev => prev + 1);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [visibleStars, starCount]);

  useEffect(() => {
    // Reset animation when score changes
    setVisibleStars(0);
  }, [score, totalQuestions]);

  return (
    <div className="flex flex-col items-center justify-center my-4">
      {/* Stars Row */}
      <div className="flex items-center justify-center gap-2 mb-2">
        {Array.from({ length: starCount }, (_, index) => {
          const starNumber = index + 1;
          return (
            <div
              key={starNumber}
              className={`transition-all duration-500 transform ${
                starNumber <= visibleStars 
                  ? 'scale-100 opacity-100' 
                  : 'scale-0 opacity-0'
              }`}
              style={{
                transitionDelay: `${index * 200}ms`
              }}
            >
              <Star 
                className={`w-8 h-8 text-yellow-400 fill-current ${
                  starNumber <= visibleStars ? 'animate-pulse' : ''
                }`}
              />
            </div>
          );
        })}
      </div>
      
      {/* Score Row */}
      <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
        {percentage}% ({score}/{totalQuestions})
      </div>
    </div>
  );
};

// Confetti Animation Component
const ConfettiAnimation = ({ isActive }) => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (isActive) {
      // Generate confetti particles
      const newParticles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: -10,
        rotation: Math.random() * 360,
        colorClass: ['bg-yellow-400', 'bg-red-400', 'bg-blue-400', 'bg-green-400', 'bg-purple-400', 'bg-pink-400'][Math.floor(Math.random() * 6)],
        size: Math.random() * 8 + 4,
        delay: Math.random() * 0.5
      }));
      setParticles(newParticles);

      // Clear particles after animation
      const timer = setTimeout(() => {
        setParticles([]);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [isActive]);

  if (!isActive || particles.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className={`absolute ${particle.colorClass} rounded-sm`}
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            transform: `rotate(${particle.rotation}deg)`,
            animation: `confetti-fall 4s ease-out forwards`,
            animationDelay: `${particle.delay}s`
          }}
        />
      ))}
    </div>
  );
};

// SpeakButton component that uses the audio file existence hook
const SpeakButton = ({ word, mediaId, speakWord, useCanSpeakWord }) => {
  const { exists } = useCanSpeakWord(word, mediaId);
  
  if (!exists) return null;
  
  return (
    <button
      onClick={() => speakWord(word, mediaId)}
      className="p-2 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
      title="Listen to pronunciation"
    >
      <Volume2 className="w-4 h-4" />
    </button>
  );
};

const WordsPage = ({ data, adsEnabled, getLevelColor, getLevelById, speakWord, useCanSpeakWord, highlightWordInExample, getPartOfSpeechColor, loadWordData, setCurrentPage }) => {
  const [quiz, setQuiz] = useState([]);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [relatedWords, setRelatedWords] = useState([]);

  // Reset quiz state when switching to a new word
  useEffect(() => {
    setSelectedAnswers({});
    setCurrentQuestion(0);
    setIsFinished(false);
    setReviewMode(false);
  }, [data.id]);

  // Fetch tags data and update SEO meta description
  useEffect(() => {
    const fetchTagsAndUpdateSEO = async () => {
      try {
        const response = await fetch('/indexes/tags.json');
        if (response.ok) {
          const tagsJson = await response.json();
          
          // Generate dynamic meta description
          const generateMetaDescription = (wordData, tagsData) => {
            if (!wordData || !wordData.words || wordData.words.length < 2) return '';
            
            const word1 = wordData.words[0]?.word || '';
            const word2 = wordData.words[1]?.word || '';
            
            // Get tag SEO descriptions
            const tagDescriptions = (wordData.tags || [])
              .map(tagId => {
                const tag = tagsData.find(t => t.id === tagId);
                return tag?.seoDescription || '';
              })
              .filter(desc => desc.length > 0);
            
            // Format tag descriptions with proper comma separation
            let tagText = '';
            if (tagDescriptions.length > 0) {
              if (tagDescriptions.length === 1) {
                tagText = tagDescriptions[0];
              } else if (tagDescriptions.length === 2) {
                tagText = tagDescriptions.join(' and ');
              } else {
                const lastTag = tagDescriptions.pop();
                tagText = tagDescriptions.join(', ') + ', and ' + lastTag;
              }
            }
            
            const baseTemplate = `Learn the difference between ${word1} and ${word2}. Master these commonly confused English words`;
            
            if (tagText) {
              return `${baseTemplate} that ${tagText}. Improve your English with clear word definitions, examples, quizzes and pronunciation guides.`;
            } else {
              return `${baseTemplate}. Improve your English with clear word definitions, examples, quizzes and pronunciation guides.`;
            }
          };
          
          const metaDescription = generateMetaDescription(data, tagsJson.data || []);
          
          // Update document title
          const documentTitle = data.title ? 
          `The difference between ${data.title.replace(' vs. ', ' and ')} | Commonly Confused Words` : `Commonly Confused Words`;
          document.title = documentTitle;
          
          // Update meta description
          if (metaDescription) {
            let metaDescElement = document.querySelector('meta[name="description"]');
            if (metaDescElement) {
              metaDescElement.setAttribute('content', metaDescription);
            } else {
              // Create meta description if it doesn't exist
              metaDescElement = document.createElement('meta');
              metaDescElement.name = 'description';
              metaDescElement.content = metaDescription;
              document.head.appendChild(metaDescElement);
            }
          }
          
          // Update Open Graph meta tags
          if (documentTitle && metaDescription) {
            // Update OG title
            let ogTitleElement = document.querySelector('meta[property="og:title"]');
            if (ogTitleElement) {
              ogTitleElement.setAttribute('content', documentTitle);
            }
            
            // Update OG description
            let ogDescElement = document.querySelector('meta[property="og:description"]');
            if (ogDescElement) {
              ogDescElement.setAttribute('content', metaDescription);
            }
            
            // Update Twitter meta tags
            let twitterTitleElement = document.querySelector('meta[property="twitter:title"]');
            if (twitterTitleElement) {
              twitterTitleElement.setAttribute('content', documentTitle);
            }
            
            let twitterDescElement = document.querySelector('meta[property="twitter:description"]');
            if (twitterDescElement) {
              twitterDescElement.setAttribute('content', metaDescription);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch tags data:', error);
      }
    };
    
    if (data?.id) {
      fetchTagsAndUpdateSEO();
    }
    
    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'Commonly Confused Words - Learn English Commonly Confused Word Pairs';
      
      // Reset meta description to default
      let metaDescElement = document.querySelector('meta[name="description"]');
      if (metaDescElement) {
        metaDescElement.setAttribute('content', 'Learn English with our interactive quizzes on commonly confused words. Master word pairs like affect vs effect, accept vs except, and more. Perfect for students and English learners.');
      }
    };
  }, [data]);

  // Fetch related word pairs
  useEffect(() => {
    const fetchRelatedWords = async () => {
      try {
        const response = await fetch('/indexes/words.json');
        if (response.ok) {
          const wordsJson = await response.json();
          const allWords = wordsJson.data || [];
          
          // Filter out current word pair
          const otherWords = allWords.filter(word => word.id !== data.id);
          
          // More robust randomization using Fisher-Yates shuffle algorithm
          const shuffleArray = (array) => {
            const shuffled = [...array];
            for (let i = shuffled.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            return shuffled;
          };
          
          // Use current time and random values for better randomization
          const timeBasedSeed = Date.now() % 1000;
          const randomSeed = Math.random() * 1000;
          
          // Apply multiple rounds of shuffling for better randomness
          let randomizedWords = shuffleArray(otherWords);
          randomizedWords = shuffleArray(randomizedWords);
          
          // Further randomize by using time-based and random offset
          const offset = Math.floor((timeBasedSeed + randomSeed) % randomizedWords.length);
          const rotatedWords = [...randomizedWords.slice(offset), ...randomizedWords.slice(0, offset)];
          
          // Select 6 random words for 2 rows of 3 cards
          const selected = rotatedWords.slice(0, 6);
          
          setRelatedWords(selected);
        }
      } catch (error) {
        console.error('Failed to fetch related words:', error);
        setRelatedWords([]);
      }
    };
    
    const handleRefresh = () => {
      fetchRelatedWords();
    };
    
    if (data?.id) {
      fetchRelatedWords();
    }
    
    // Listen for refresh events
    window.addEventListener('refreshRelatedWords', handleRefresh);
    
    return () => {
      window.removeEventListener('refreshRelatedWords', handleRefresh);
    };
  }, [data.id]);

  useEffect(() => {
    let isCancelled = false;
    const fetchQuiz = async () => {
      try {
        const res = await fetch(`/data/quizzes/${data.id}.json`);
        if (!res.ok) return;
        const json = await res.json();
        if (!isCancelled) setQuiz(Array.isArray(json.quiz) ? json.quiz : []);
      } catch (_) {
        if (!isCancelled) setQuiz([]);
      }
    };
    fetchQuiz();
    return () => {
      isCancelled = true;
    };
  }, [data.id]);

  const selectAnswer = (qIndex, option) => {
    setSelectedAnswers(prev => ({ ...prev, [qIndex]: option }));
  };

  const correctCount = quiz.reduce((acc, q, i) => acc + (selectedAnswers[i] === q.answer ? 1 : 0), 0);

  const handleNext = () => {
    if (currentQuestion < quiz.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setIsFinished(true);
    }
  };

  const handleRestart = () => {
    setSelectedAnswers({});
    setCurrentQuestion(0);
    setIsFinished(false);
    setReviewMode(false);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {adsEnabled && (
        <div className="my-6 flex justify-center">
          {/* Google AdSense Placeholder (Top) */}
          <div className="w-full max-w-md h-24 bg-gray-200 border border-gray-300 rounded flex items-center justify-center text-gray-500 text-sm">
            AdSense Placeholder (Top)
          </div>
        </div>
      )}
      <div className="mb-8">
        <button
          onClick={() => window.history.back()}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors mb-4 inline-flex items-center no-underline bg-transparent border-0 p-0 cursor-pointer hover:underline"
          type="button"
        >
          <ArrowLeft className="w-6 h-6 mr-2" />
          Back
        </button>
        
        <div className="mb-8">          
          {/* Word pair title */}
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
              What is the difference between<br />
              <strong>{data.words[0].word}</strong> and <strong>{data.words[1].word}</strong>?
            </h1>
          </div>

          <div className="flex justify-center mb-4">
            <a
              href={`/levels/${data.level}/`}
              className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(getLevelById(data.level)?.code)} hover:underline cursor-pointer`}
            >
              {getLevelById(data.level)?.code} - {getLevelById(data.level)?.name}
            </a>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {data.words.map((word, index) => (
          <div
            key={index}
            className="flex-1 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {word.word}
                </h2>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPartOfSpeechColor(word.partOfSpeech)}`}>
                  {word.partOfSpeech}
                </span>
              </div>
              
              <div className="flex items-center gap-3 mb-4">
                <span className="text-gray-600 dark:text-gray-400 font-mono">
                  {word.pronunciation}
                </span>
                <SpeakButton 
                  word={word.word} 
                  mediaId={word.mediaId || null} 
                  speakWord={speakWord}
                  useCanSpeakWord={useCanSpeakWord}
                />
              </div>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Definition</h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {word.definition}
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Examples</h3>
              <div className="space-y-2">
                {word.examples.map((example, exIndex) => (
                  <p
                    key={exIndex}
                    className="text-gray-600 dark:text-gray-400 leading-relaxed p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    dangerouslySetInnerHTML={{
                      __html: highlightWordInExample(example, word.word)
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Other Pronunciations */}
            {word.otherPronunciations && word.otherPronunciations.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Other Pronunciations</h3>
                <div className="space-y-4">
                  {word.otherPronunciations.map((altPron, altIndex) => (
                                                                                    <div key={altIndex} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border-l-4 border-blue-500">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-gray-600 dark:text-gray-400 font-mono text-sm">
                                        {altPron.pronunciation}
                                      </span>
                                      <SpeakButton 
                                        word={word.word} 
                                        mediaId={altPron.mediaId || null} 
                                        speakWord={speakWord}
                                        useCanSpeakWord={useCanSpeakWord}
                                      />
                                    </div>
                                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${getPartOfSpeechColor(altPron.partOfSpeech)}`}>
                                      {altPron.partOfSpeech}
                                    </span>
                                  </div>
                                  <div className="mb-2">
                                    <h4 className="font-medium text-gray-700 dark:text-gray-300 text-sm mb-1">Definition</h4>
                                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                                      {altPron.definition}
                                    </p>
                                  </div>
                                  {altPron.examples && altPron.examples.length > 0 && (
                                    <div>
                                      <h4 className="font-medium text-gray-700 dark:text-gray-300 text-sm mb-1">Examples</h4>
                                      <div className="space-y-1">
                                        {altPron.examples.map((example, exIndex) => (
                                          <p
                                            key={exIndex}
                                            className="text-gray-500 dark:text-gray-400 text-sm italic p-2 bg-white dark:bg-gray-600 rounded"
                                            dangerouslySetInnerHTML={{
                                              __html: highlightWordInExample(example, word.word)
                                            }}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {data.tags && data.tags.length > 0 && (
        <div className="mt-8 p-6 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {data.tags.map((tag) => (
              <a
                key={tag}
                href={`/tags/${tag}/`}
                className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 rounded-md text-xs hover:underline cursor-pointer"
              >
                {tag.replace('-', ' ')}
              </a>
            ))}
          </div>
        </div>
      )}

      {adsEnabled && (
        <div className="my-6 flex justify-center">
          {/* Google AdSense Placeholder (Bottom) */}
          <div className="w-full max-w-md h-24 bg-gray-200 border border-gray-300 rounded flex items-center justify-center text-gray-500 text-sm">
            AdSense Placeholder (Bottom)
          </div>
        </div>
      )}

      {/* Quiz Section */}
      {quiz && quiz.length > 0 && (
        <div className="mt-10 p-6 bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Practice Quiz</h3>
          {!isFinished && !reviewMode && (
            <div className="text-left">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Question {currentQuestion + 1} of {quiz.length}</p>
              <p className="font-medium text-gray-900 dark:text-white mb-4">{quiz[currentQuestion].question}</p>
              <div className="flex flex-wrap gap-2">
                {quiz[currentQuestion].options.map(opt => {
                  const isSelected = selectedAnswers[currentQuestion] === opt;
                  const classes = [
                    'px-3 py-1 rounded-full border text-sm',
                    'transition-colors',
                    'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                    'text-gray-800 dark:text-gray-200',
                  ];
                  if (isSelected) classes.push('ring-2 ring-blue-400');
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => selectAnswer(currentQuestion, opt)}
                      className={classes.join(' ')}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!selectedAnswers[currentQuestion]}
                  className={`px-4 py-2 rounded-md text-white ${selectedAnswers[currentQuestion] ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 cursor-not-allowed'}`}
                >
                  {currentQuestion < quiz.length - 1 ? 'Next' : 'Finish'}
                </button>
              </div>
            </div>
          )}

          {isFinished && !reviewMode && (
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-900 dark:text-white">Quiz Complete!</p>
              
              {/* Animated Stars Rating */}
              <AnimatedStars score={correctCount} totalQuestions={quiz.length} />
              
              {/* Confetti for Perfect Score */}
              <ConfettiAnimation isActive={correctCount === quiz.length && quiz.length > 0} />
              
              {/* Motivational Message */}
              {(() => {
                const percentage = Math.round((correctCount / quiz.length) * 100);
                let message = "";
                let messageColor = "";
                
                if (percentage === 100) {
                  message = "🎉 Perfect Score! Outstanding work!";
                  messageColor = "text-green-600 dark:text-green-400";
                } else if (percentage >= 76) {
                  message = "🌟 Excellent job! You've mastered these words!";
                  messageColor = "text-green-600 dark:text-green-400";
                } else if (percentage >= 51) {
                  message = "👍 Good work! Keep practicing to improve!";
                  messageColor = "text-blue-600 dark:text-blue-400";
                } else if (percentage > 0) {
                  message = "💪 Keep trying! Practice makes perfect!";
                  messageColor = "text-orange-600 dark:text-orange-400";
                } else {
                  message = "📚 Don't give up! Review the words and try again!";
                  messageColor = "text-red-600 dark:text-red-400";
                }
                
                return (
                  <p className={`mt-2 text-sm font-medium ${messageColor}`}>
                    {message}
                  </p>
                );
              })()}
              
              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setReviewMode(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Review Answers
                </button>
                <button
                  type="button"
                  onClick={handleRestart}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Restart Quiz
                </button>
              </div>
            </div>
          )}

          {reviewMode && (
            <div className="space-y-6 text-left">
              {quiz.map((q, i) => {
                const selected = selectedAnswers[i];
                const isCorrect = selected === q.answer;
                return (
                  <div key={i}>
                    <p className="font-medium text-gray-900 dark:text-white mb-2">{i + 1}. {q.question}</p>
                    <div className="flex flex-wrap gap-2">
                      {q.options.map(opt => {
                        const selectedThis = selected === opt;
                        const classes = [
                          'px-3 py-1 rounded-full border text-sm',
                          'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                          'text-gray-800 dark:text-white',
                        ];
                        if (selectedThis) classes.push(isCorrect ? 'bg-green-100 dark:bg-green-900 border-green-300 dark:border-green-700 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900 border-red-300 dark:border-red-700 text-red-800 dark:text-red-200');
                        if (!selectedThis && opt === q.answer) classes.push('ring-2 ring-green-400');
                        return (
                          <span key={opt} className={classes.join(' ')}>{opt}</span>
                        );
                      })}
                    </div>
                    {!selected && (
                      <p className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">You did not answer this question.</p>
                    )}
                    {selected && !isCorrect && (
                      <p className="mt-2 text-sm text-red-700 dark:text-red-300">Correct answer: {q.answer}</p>
                    )}
                  </div>
                );
              })}
              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setReviewMode(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Back to Results
                </button>
                <button
                  type="button"
                  onClick={handleRestart}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Restart Quiz
                </button>
              </div>
            </div>
          )}
        </div>
      )}


      {adsEnabled && (
        <div className="my-6 flex justify-center">
          {/* Google AdSense Placeholder (Bottom) */}
          <div className="w-full max-w-md h-24 bg-gray-200 border border-gray-300 rounded flex items-center justify-center text-gray-500 text-sm">
            AdSense Placeholder (Bottom)
          </div>
        </div>
      )}

      {/* Related Word Pairs Section */}
      {relatedWords.length > 0 && (
        <div className="mt-12 border-t border-gray-200 dark:border-gray-700 pt-8">
          <div className="flex items-center justify-center gap-4 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Other Word Pairs to Explore
            </h2>
            <button
              onClick={() => {
                // Directly dispatch refresh event without clearing state to prevent flicker
                const event = new CustomEvent('refreshRelatedWords');
                window.dispatchEvent(event);
              }}
              className="p-2 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
              title="Get different word pairs"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {relatedWords
              .sort((a, b) => (b.rank || 0) - (a.rank || 0)) // Sort by rank descending (highest first)
              .map((word) => (
              <div
                key={word.id}
                className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-gray-700 hover:scale-105"
              >
                {/* Level */}
                <div className="flex justify-start mb-2">
                  <a
                    href={`/levels/${word.level}/`}
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(getLevelById(word.level)?.code)} hover:underline cursor-pointer`}
                  >
                    {getLevelById(word.level)?.code} - {getLevelById(word.level)?.name}
                  </a>
                </div>
                
                {/* Word pair title */}
                <div className="text-center py-4">
                  <a
                    href={`/${word.id}/`}
                    className="cursor-pointer"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-colors">
                      {word.title}
                    </h3>
                  </a>
                </div>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {word.tags && word.tags.map((tag) => (
                    <a
                      key={tag}
                      href={`/tags/${tag}/`}
                      className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 rounded-md text-xs hover:underline cursor-pointer"
                    >
                      {tag.replace('-', ' ')}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
  };

// WordPairPopup component for showing word pair details
const WordPairPopup = ({ wordPairId, onClose, getLevelColor, getLevelById, speakWord, canSpeakWord, highlightWordInExample, getPartOfSpeechColor }) => {
  const [wordData, setWordData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWordData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/data/words/${wordPairId}.json`);
        if (response.ok) {
          const data = await response.json();
          setWordData(data);
        }
      } catch (err) {
        console.error('Failed to load word data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (wordPairId) {
      fetchWordData();
    }
  }, [wordPairId]);

  if (!wordPairId) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {loading ? 'Loading...' : wordData?.title || 'Word Pair Details'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Loading word data...</p>
            </div>
          ) : wordData ? (
            <div className="flex flex-col lg:flex-row gap-8">
              {wordData.words.map((word, index) => (
                <div
                  key={index}
                  className="flex-1 bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700"
                >
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {word.word}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPartOfSpeechColor(word.partOfSpeech)}`}>
                        {word.partOfSpeech}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-gray-600 dark:text-gray-400 font-mono text-sm">
                        {word.pronunciation}
                      </span>
                      {canSpeakWord(word.word, word.mediaId || null) && (
                        <button
                          onClick={() => speakWord(word.word, word.mediaId || null)}
                          className="p-1.5 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                          title="Listen to pronunciation"
                        >
                          <Volume2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Definition</h4>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      {word.definition}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Examples</h4>
                    <div className="space-y-2">
                      {word.examples.map((example, exIndex) => (
                        <p
                          key={exIndex}
                          className="text-gray-600 dark:text-gray-400 leading-relaxed p-3 bg-white dark:bg-gray-700 rounded-lg text-sm"
                          dangerouslySetInnerHTML={{
                            __html: highlightWordInExample(example, word.word)
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Other Pronunciations */}
                  {word.otherPronunciations && word.otherPronunciations.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Other Pronunciations</h4>
                      <div className="space-y-3">
                        {word.otherPronunciations.map((altPron, altIndex) => (
                                                                                          <div key={altIndex} className="p-3 bg-white dark:bg-gray-700 rounded-lg border-l-4 border-blue-500">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-gray-600 dark:text-gray-400 font-mono text-sm">
                                        {altPron.pronunciation}
                                      </span>
                                      {canSpeakWord(word.word, altPron.mediaId || null) && (
                                        <button
                                          onClick={() => speakWord(word.word, altPron.mediaId || null)}
                                          className="p-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                                          title="Listen to pronunciation"
                                        >
                                          <Volume2 className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${getPartOfSpeechColor(altPron.partOfSpeech)}`}>
                                      {altPron.partOfSpeech}
                                    </span>
                                  </div>
                                  <div className="mb-2">
                                    <h5 className="font-medium text-gray-700 dark:text-gray-300 text-sm mb-1">Definition</h5>
                                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                                      {altPron.definition}
                                    </p>
                                  </div>
                                  {altPron.examples && altPron.examples.length > 0 && (
                                    <div>
                                      <h5 className="font-medium text-gray-700 dark:text-gray-300 text-sm mb-1">Examples</h5>
                                      <div className="space-y-1">
                                        {altPron.examples.map((example, exIndex) => (
                                          <p
                                            key={exIndex}
                                            className="text-gray-500 dark:text-gray-400 text-sm italic p-2 bg-gray-50 dark:bg-gray-600 rounded"
                                            dangerouslySetInnerHTML={{
                                              __html: highlightWordInExample(example, word.word)
                                            }}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400">Failed to load word data.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Word Details Popup Component
const WordDetailsPopup = ({ word, onClose, speakWord, canSpeakWord, getPartOfSpeechColor }) => {
  // Prevent body scroll when popup is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (!word) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {word.word}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Part of Speech */}
          <div className="flex justify-end">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPartOfSpeechColor(word.partOfSpeech)}`}>
              {word.partOfSpeech || 'N/A'}
            </span>
          </div>
        </div>
        
        <div className="p-6">
          {/* Pronunciation */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-gray-600 dark:text-gray-400 font-mono text-sm">
              {word.pronunciation}
            </span>
            {canSpeakWord(word.word, word.mediaId || null) && (
              <button
                onClick={() => speakWord(word.word, word.mediaId || null)}
                className="p-2 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                title="Listen to pronunciation"
              >
                <Volume2 className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Definition */}
          <div className="mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Definition</h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              {word.definition}
            </p>
          </div>

          {/* Examples */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Examples</h3>
            <div className="space-y-2">
              {word.examples.map((example, index) => (
                <p
                  key={index}
                  className="text-gray-600 dark:text-gray-400 leading-relaxed p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm"
                  dangerouslySetInnerHTML={{
                    __html: example.replaceAll('[', '<strong>').replaceAll(']', '</strong>')
                  }}
                />
              ))}
            </div>
          </div>

          {/* Other Pronunciations */}
          {word.otherPronunciations && word.otherPronunciations.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Other Pronunciations</h3>
              <div className="space-y-3">
                {word.otherPronunciations.map((altPron, altIndex) => (
                                                                              <div key={altIndex} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border-l-4 border-blue-500">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-600 dark:text-gray-400 font-mono text-sm">
                                      {altPron.pronunciation}
                                    </span>
                                    {canSpeakWord(word.word, altPron.mediaId || null) && (
                                      <button
                                        onClick={() => speakWord(word.word, altPron.mediaId || null)}
                                        className="p-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                                        title="Listen to pronunciation"
                                      >
                                        <Volume2 className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                  <span className={`px-2 py-1 rounded-full text-sm font-medium ${getPartOfSpeechColor(altPron.partOfSpeech)}`}>
                                    {altPron.partOfSpeech}
                                  </span>
                                </div>
                                <div className="mb-2">
                                  <h5 className="font-medium text-gray-700 dark:text-gray-300 text-sm mb-1">Definition</h5>
                                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                                    {altPron.definition}
                                  </p>
                                </div>
                                {altPron.examples && altPron.examples.length > 0 && (
                                  <div>
                                    <h5 className="font-medium text-gray-700 dark:text-gray-300 text-sm mb-1">Examples</h5>
                                    <div className="space-y-1">
                                      {altPron.examples.map((example, exIndex) => (
                                        <p
                                          key={exIndex}
                                          className="text-gray-500 dark:text-gray-400 text-sm italic p-2 bg-white dark:bg-gray-600 rounded"
                                          dangerouslySetInnerHTML={{
                                            __html: example.replaceAll('[', '<strong>').replaceAll(']', '</strong>')
                                          }}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Non-scrollable Footer with Close Button */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex justify-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Flashcard Component
const FlashCard = ({ word, definition, examples, pronunciation, mediaId, partOfSpeech, speakWord, canSpeakWord, isFlipped, onFlip, confusedWith, confusedWithData, showHint, onToggleHint, onShowConfusedWord, getPartOfSpeechColor }) => {
  return (
    <div className="relative w-full h-96 perspective-1000">
      <div 
        className={`relative w-full h-full transition-transform duration-700 transform-style-preserve-3d cursor-pointer ${isFlipped ? 'rotate-y-180' : ''}`}
        onClick={onFlip}
      >
        {/* Front of card - Definition */}
        <div className="absolute inset-0 w-full h-full backface-hidden bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 flex flex-col justify-center">
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Definition</h3>
            <p className="text-xl text-gray-900 dark:text-white leading-relaxed mb-4">
              {definition}
            </p>
            
            {/* Hint Section */}
            <div className="mb-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleHint();
                }}
                className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300 rounded-md text-sm hover:bg-yellow-200 dark:hover:bg-yellow-800 transition-colors"
              >
                {showHint ? 'Hide Hint' : 'Show Hint'}
              </button>
              
              {showHint && confusedWith && (
                <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    Often confused with <strong>{confusedWith}</strong>
                  </p>
                </div>
              )}
            </div>
            
            <p className="text-sm text-gray-500 dark:text-gray-400">Click to see the word</p>
          </div>
        </div>

        {/* Back of card - Word and Examples */}
        <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex flex-col h-full">
            {/* Part of Speech - Top Right */}
            <div className="flex justify-end mb-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPartOfSpeechColor(partOfSpeech)}`}>
                {partOfSpeech || 'N/A'}
              </span>
            </div>
            {/* Word and pronunciation */}
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">{word}</h2>
              
              {/* Often confused with */}
              {confusedWith && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  Often confused with{" "}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowConfusedWord();
                    }}
                    className="underline hover:no-underline font-bold text-blue-600 dark:text-blue-400"
                  >
                    {confusedWith}
                  </button>
                </p>
              )}
              
              <div className="flex items-center justify-center gap-3">
                <span className="text-gray-600 dark:text-gray-400 font-mono text-sm">
                  {pronunciation}
                </span>
                {canSpeakWord(word, mediaId || null) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      speakWord(word, mediaId || null);
                    }}
                    className="p-2 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors"
                    title="Listen to pronunciation"
                  >
                    <Volume2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Examples */}
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Examples</h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {examples.slice(0, 2).map((example, index) => (
                  <p
                    key={index}
                    className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed p-2 bg-gray-50 dark:bg-gray-700 rounded"
                    dangerouslySetInnerHTML={{
                      __html: example.replaceAll('[', '<strong>').replaceAll(']', '</strong>')
                    }}
                  />
                ))}
              </div>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 text-center">Click to see definition</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const FlashcardsPage = ({ levels, getLevelColor, speakWord, canSpeakWord, highlightWordInExample, getPartOfSpeechColor, getLevelById }) => {
  const [flashcards, setFlashcards] = useState([]);
  const [numFlashcards, setNumFlashcards] = useState(10);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableWords, setAvailableWords] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [flashcardsGenerated, setFlashcardsGenerated] = useState(false);
  const [currentFlashcard, setCurrentFlashcard] = useState(0);
  const [flippedStates, setFlippedStates] = useState({});
  const [hintStates, setHintStates] = useState({});
  const [showWordPopup, setShowWordPopup] = useState(false);
  const [popupWordData, setPopupWordData] = useState(null);
  const [availableQuestions, setAvailableQuestions] = useState(0);

  // Load available words and tags on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [wordsRes, tagsRes] = await Promise.all([
          fetch('/indexes/words.json'),
          fetch('/indexes/tags.json')
        ]);
        const wordsJson = await wordsRes.json();
        const tagsJson = await tagsRes.json();
        setAvailableWords(wordsJson.data || []);
        setAllTags(tagsJson.data || []);
      } catch (err) {
        console.error('Failed to load word data:', err);
      }
    };
    fetchData();
  }, []);

  // Filter words based on selected criteria
  const getFilteredWords = useCallback(() => {
    return availableWords.filter(wordPair => {
      const levelMatch = selectedLevels.length === 0 || selectedLevels.includes(wordPair.level);
      const tagMatch = selectedTags.length === 0 || selectedTags.some(tag => wordPair.tags && wordPair.tags.includes(tag));
      return levelMatch && tagMatch;
    });
  }, [availableWords, selectedLevels, selectedTags]);

  // Update available questions count when filters change
  useEffect(() => {
    const filteredWords = getFilteredWords();
    // Each word pair has 2 words, so 2 possible flashcards
    setAvailableQuestions(filteredWords.length * 2);
  }, [selectedLevels, selectedTags, availableWords, getFilteredWords]);

  // Generate flashcards
  const generateFlashcards = async () => {
    setIsGenerating(true);
    try {
      const filteredWords = getFilteredWords();
      if (filteredWords.length === 0) {
        alert('No word pairs match your criteria. Please adjust your filters.');
        setIsGenerating(false);
        return;
      }

      // Collect all words from all matching word pairs
      const allWords = [];
      for (const wordPair of filteredWords) {
        try {
          const response = await fetch(`/data/words/${wordPair.id}.json`);
          if (response.ok) {
            const wordData = await response.json();
            if (wordData.words && wordData.words.length >= 2) {
              wordData.words.forEach((word, index) => {
                // Find the other word in the pair for the "confused with" hint
                const otherWordIndex = index === 0 ? 1 : 0;
                const confusedWithData = wordData.words[otherWordIndex] || null;
                
                allWords.push({
                  ...word,
                  wordPairId: wordPair.id,
                  wordPairTitle: wordPair.title,
                  confusedWith: confusedWithData?.word || null,
                  confusedWithData: confusedWithData
                });
              });
            }
          }
        } catch (err) {
          console.error(`Failed to load word data for ${wordPair.id}:`, err);
        }
      }

      // Randomly select the requested number of flashcards
      const shuffled = allWords.sort(() => 0.5 - Math.random());
      const selectedFlashcards = shuffled.slice(0, Math.min(numFlashcards, allWords.length));

      setFlashcards(selectedFlashcards);
      setFlashcardsGenerated(true);
      setCurrentFlashcard(0);
      setFlippedStates({});
      setHintStates({});
    } catch (err) {
      console.error('Failed to generate flashcards:', err);
      alert('Failed to generate flashcards. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Flashcard navigation
  const nextFlashcard = () => {
    if (currentFlashcard < flashcards.length - 1) {
      const nextIndex = currentFlashcard + 1;
      // Update both states simultaneously in a batched update
      setCurrentFlashcard(nextIndex);
      setFlippedStates(prev => ({
        ...prev,
        [nextIndex]: false
      }));
      setHintStates(prev => ({
        ...prev,
        [nextIndex]: false
      }));
    }
  };

  const prevFlashcard = () => {
    if (currentFlashcard > 0) {
      const prevIndex = currentFlashcard - 1;
      // Update both states simultaneously in a batched update
      setCurrentFlashcard(prevIndex);
      setFlippedStates(prev => ({
        ...prev,
        [prevIndex]: false
      }));
      setHintStates(prev => ({
        ...prev,
        [prevIndex]: false
      }));
    }
  };

  const flipCard = () => {
    setFlippedStates(prev => ({
      ...prev,
      [currentFlashcard]: !prev[currentFlashcard]
    }));
  };

  const toggleHint = () => {
    setHintStates(prev => ({
      ...prev,
      [currentFlashcard]: !prev[currentFlashcard]
    }));
  };

  const showConfusedWord = () => {
    if (flashcards[currentFlashcard]?.confusedWithData) {
      setPopupWordData(flashcards[currentFlashcard].confusedWithData);
      setShowWordPopup(true);
    }
  };

  const closeWordPopup = () => {
    setShowWordPopup(false);
    setPopupWordData(null);
  };

  const resetFlashcards = () => {
    setFlashcards([]);
    setFlashcardsGenerated(false);
    setCurrentFlashcard(0);
    setFlippedStates({});
    setHintStates({});
    // Clear settings
    setNumFlashcards(10);
    setSelectedLevels([]);
    setSelectedTags([]);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {!flashcardsGenerated && (
        <div className="mb-8">        
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
              Flashcards
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Study word definitions with interactive flashcards
            </p>
          </div>
        </div>
      )}

      {!flashcardsGenerated ? (
        // Flashcard Configuration
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-6">
            <GraduationCap className="w-6 h-6 text-green-600 dark:text-green-400" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Flashcard Settings</h2>
          </div>

          {/* Levels */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Levels (leave empty for all levels)
            </label>
            <div className="flex flex-wrap gap-2">
              {levels.map(level => (
                <button
                  key={level.id}
                  onClick={() => {
                    setSelectedLevels(prev => 
                      prev.includes(level.id) 
                        ? prev.filter(l => l !== level.id)
                        : [...prev, level.id]
                    );
                  }}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    selectedLevels.includes(level.id)
                      ? `${getLevelColor(level.code)} ring-2 ring-green-400`
                      : `${getLevelColor(level.code)} hover:ring-2 hover:ring-gray-300`
                  }`}
                >
                  {level.code} - {level.name}
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Tags (leave empty for all tags)
            </label>
            <div className="flex flex-wrap gap-2">
              {allTags.map(tag => (
                <button
                  key={tag.id}
                  onClick={() => {
                    setSelectedTags(prev => 
                      prev.includes(tag.id) 
                        ? prev.filter(t => t !== tag.id)
                        : [...prev, tag.id]
                    );
                  }}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedTags.includes(tag.id)
                      ? 'bg-green-600 text-white'
                      : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800'
                  }`}
                >
                  {tag.title}
                </button>
              ))}
            </div>
          </div>

          {/* Number of Flashcards */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Number of Flashcards
            </label>
            <div className="flex flex-wrap gap-2">
              {[5, 10, 20, 30].map(num => (
                <button
                  key={num}
                  onClick={() => setNumFlashcards(num)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    numFlashcards === num
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong>Available flashcards matching your criteria:</strong> {availableQuestions}
            </p>
          </div>

          {/* Generate Button */}
          <button
            onClick={generateFlashcards}
            disabled={isGenerating || getFilteredWords().length === 0}
            className={`w-full py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
              isGenerating || getFilteredWords().length === 0
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Generating Flashcards...
              </>
            ) : (
              <>
                <GraduationCap className="w-5 h-5" />
                Generate Flashcards
              </>
            )}
          </button>
        </div>
      ) : (
        // Flashcards Display
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-6 h-6 text-green-600 dark:text-green-400" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Flashcards</h2>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {currentFlashcard + 1} of {flashcards.length}
              </span>
              <button
                onClick={resetFlashcards}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                New Flashcards
              </button>
            </div>
          </div>

          {/* Flashcard */}
          {flashcards.length > 0 && (
            <div className="mb-6">
              <FlashCard
                key={currentFlashcard} // Force re-render when card changes
                word={flashcards[currentFlashcard].word}
                definition={flashcards[currentFlashcard].definition}
                examples={flashcards[currentFlashcard].examples}
                pronunciation={flashcards[currentFlashcard].pronunciation}
                mediaId={flashcards[currentFlashcard].mediaId}
                partOfSpeech={flashcards[currentFlashcard].partOfSpeech}
                speakWord={speakWord}
                canSpeakWord={canSpeakWord}
                isFlipped={flippedStates[currentFlashcard] || false}
                onFlip={flipCard}
                confusedWith={flashcards[currentFlashcard].confusedWith}
                confusedWithData={flashcards[currentFlashcard].confusedWithData}
                showHint={hintStates[currentFlashcard] || false}
                onToggleHint={toggleHint}
                onShowConfusedWord={showConfusedWord}
                getPartOfSpeechColor={getPartOfSpeechColor}
              />
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={prevFlashcard}
              disabled={currentFlashcard === 0}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                currentFlashcard === 0
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Previous
            </button>
            <button
              onClick={flipCard}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
            >
              Flip Card
            </button>
            <button
              onClick={nextFlashcard}
              disabled={currentFlashcard === flashcards.length - 1}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                currentFlashcard === flashcards.length - 1
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Word Details Popup */}
      {showWordPopup && (
        <WordDetailsPopup
          word={popupWordData}
          onClose={closeWordPopup}
          speakWord={speakWord}
          canSpeakWord={canSpeakWord}
          getPartOfSpeechColor={getPartOfSpeechColor}
        />
      )}
    </div>
  );
};

const QuizzesPage = ({ levels, getLevelColor, speakWord, canSpeakWord, highlightWordInExample, getPartOfSpeechColor, getLevelById }) => {
  const [customQuiz, setCustomQuiz] = useState([]);
  const [numQuestions, setNumQuestions] = useState(10);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableQuizzes, setAvailableQuizzes] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [quizGenerated, setQuizGenerated] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [isFinished, setIsFinished] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [availableQuestions, setAvailableQuestions] = useState(0);
  const [showLearnPopup, setShowLearnPopup] = useState(null);

  // Load available quizzes and tags on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [quizzesRes, tagsRes] = await Promise.all([
          fetch('/indexes/quizzes.json'),
          fetch('/indexes/tags.json')
        ]);
        const quizzesJson = await quizzesRes.json();
        const tagsJson = await tagsRes.json();
        setAvailableQuizzes(quizzesJson.data || []);
        setAllTags(tagsJson.data || []);
      } catch (err) {
        console.error('Failed to load quiz data:', err);
      }
    };
    fetchData();
  }, []);

  // Filter quizzes based on selected criteria
  const getFilteredQuizzes = useCallback(() => {
    return availableQuizzes.filter(quiz => {
      const levelMatch = selectedLevels.length === 0 || selectedLevels.includes(quiz.level);
      const tagMatch = selectedTags.length === 0 || selectedTags.some(tag => quiz.tags.includes(tag));
      return levelMatch && tagMatch;
    });
  }, [availableQuizzes, selectedLevels, selectedTags]);

  // Update available questions count when filters change
  useEffect(() => {
    const updateAvailableQuestions = async () => {
      const filteredQuizzes = getFilteredQuizzes();
      if (filteredQuizzes.length === 0) {
        setAvailableQuestions(0);
        return;
      }

      try {
        // Fetch all matching quiz data to count questions
        const quizDataPromises = filteredQuizzes.map(quiz => 
          fetch(`/data/quizzes/${quiz.id}.json`).then(res => res.json())
        );
        const allQuizData = await Promise.all(quizDataPromises);

        // Count total questions across all matching quizzes
        let totalQuestions = 0;
        allQuizData.forEach(quizData => {
          if (quizData.quiz && Array.isArray(quizData.quiz)) {
            totalQuestions += quizData.quiz.length;
          }
        });

        setAvailableQuestions(totalQuestions);
      } catch (err) {
        console.error('Failed to count available questions:', err);
        setAvailableQuestions(0);
      }
    };

    updateAvailableQuestions();
  }, [selectedLevels, selectedTags, availableQuizzes, getFilteredQuizzes]);

  // Generate custom quiz
  const generateQuiz = async () => {
    setIsGenerating(true);
    try {
      const filteredQuizzes = getFilteredQuizzes();
      if (filteredQuizzes.length === 0) {
        alert('No quizzes match your criteria. Please adjust your filters.');
        setIsGenerating(false);
        return;
      }

      // Fetch all matching quiz data
      const quizDataPromises = filteredQuizzes.map(quiz => 
        fetch(`/data/quizzes/${quiz.id}.json`).then(res => res.json())
      );
      const allQuizData = await Promise.all(quizDataPromises);

      // Collect all questions from all matching quizzes with source info
      const allQuestions = [];
      allQuizData.forEach((quizData, index) => {
        if (quizData.quiz && Array.isArray(quizData.quiz)) {
          const sourceQuiz = filteredQuizzes[index];
          quizData.quiz.forEach(question => {
            allQuestions.push({
              ...question,
              sourceWordPairId: quizData.id || sourceQuiz.id
            });
          });
        }
      });

      // Randomly select the requested number of questions
      const shuffled = allQuestions.sort(() => 0.5 - Math.random());
      const selectedQuestions = shuffled.slice(0, Math.min(numQuestions, allQuestions.length));

      setCustomQuiz(selectedQuestions);
      setQuizGenerated(true);
      setCurrentQuestion(0);
      setSelectedAnswers({});
      setIsFinished(false);
      setReviewMode(false);
    } catch (err) {
      console.error('Failed to generate quiz:', err);
      alert('Failed to generate quiz. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Quiz logic (similar to existing quiz functionality)
  const selectAnswer = (qIndex, option) => {
    setSelectedAnswers(prev => ({ ...prev, [qIndex]: option }));
  };

  const correctCount = customQuiz.reduce((acc, q, i) => acc + (selectedAnswers[i] === q.answer ? 1 : 0), 0);

  const handleNext = () => {
    if (currentQuestion < customQuiz.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setIsFinished(true);
    }
  };

  const handleRestart = () => {
    setSelectedAnswers({});
    setCurrentQuestion(0);
    setIsFinished(false);
    setReviewMode(false);
  };

  const resetQuiz = () => {
    setCustomQuiz([]);
    setQuizGenerated(false);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setIsFinished(false);
    setReviewMode(false);
    // Clear quiz settings
    setNumQuestions(10);
    setSelectedLevels([]);
    setSelectedTags([]);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {!quizGenerated && (
        <div className="mb-8">        
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
              Custom Quiz
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Create a personalized quiz by selecting your preferences
            </p>
          </div>
        </div>
      )}

      {!quizGenerated ? (
        // Quiz Configuration
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-6">
            <Settings className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Quiz Settings</h2>
          </div>

          {/* Levels */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Levels (leave empty for all levels)
            </label>
            <div className="flex flex-wrap gap-2">
              {levels.map(level => (
                <button
                  key={level.id}
                  onClick={() => {
                    setSelectedLevels(prev => 
                      prev.includes(level.id) 
                        ? prev.filter(l => l !== level.id)
                        : [...prev, level.id]
                    );
                  }}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    selectedLevels.includes(level.id)
                      ? `${getLevelColor(level.code)} ring-2 ring-blue-400`
                      : `${getLevelColor(level.code)} hover:ring-2 hover:ring-gray-300`
                  }`}
                >
                  {level.code} - {level.name}
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Tags (leave empty for all tags)
            </label>
            <div className="flex flex-wrap gap-2">
              {allTags.map(tag => (
                <button
                  key={tag.id}
                  onClick={() => {
                    setSelectedTags(prev => 
                      prev.includes(tag.id) 
                        ? prev.filter(t => t !== tag.id)
                        : [...prev, tag.id]
                    );
                  }}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedTags.includes(tag.id)
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800'
                  }`}
                >
                  {tag.title}
                </button>
              ))}
            </div>
          </div>

          {/* Number of Questions */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Number of Questions
            </label>
            <div className="flex flex-wrap gap-2">
              {[5, 10, 20, 30].map(num => (
                <button
                  key={num}
                  onClick={() => setNumQuestions(num)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    numQuestions === num
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>

          {/* Quiz Info */}
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong>Available questions matching your criteria:</strong> {availableQuestions}
            </p>

          </div>

          {/* Generate Button */}
          <button
            onClick={generateQuiz}
            disabled={isGenerating || getFilteredQuizzes().length === 0}
            className={`w-full py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
              isGenerating || getFilteredQuizzes().length === 0
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Generating Quiz...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Generate Quiz
              </>
            )}
          </button>
        </div>
      ) : (
        // Quiz Display (similar to existing quiz logic)
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Brain className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Custom Quiz</h2>
            </div>
            <button
              onClick={resetQuiz}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              New Quiz
            </button>
          </div>

          {!isFinished && !reviewMode && (
            <div className="text-left">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Question {currentQuestion + 1} of {customQuiz.length}</p>
              <p className="font-medium text-gray-900 dark:text-white mb-4">{customQuiz[currentQuestion]?.question}</p>
              <div className="flex flex-wrap gap-2">
                {customQuiz[currentQuestion]?.options.map(opt => {
                  const isSelected = selectedAnswers[currentQuestion] === opt;
                  const classes = [
                    'px-3 py-1 rounded-full border text-sm',
                    'transition-colors',
                    'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                    'text-gray-800 dark:text-gray-200',
                  ];
                  if (isSelected) classes.push('ring-2 ring-blue-400');
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => selectAnswer(currentQuestion, opt)}
                      className={classes.join(' ')}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!selectedAnswers[currentQuestion]}
                  className={`px-4 py-2 rounded-md text-white ${selectedAnswers[currentQuestion] ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 cursor-not-allowed'}`}
                >
                  {currentQuestion < customQuiz.length - 1 ? 'Next' : 'Finish'}
                </button>
              </div>
            </div>
          )}

          {isFinished && !reviewMode && (
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-900 dark:text-white">Quiz Complete!</p>
              
              {/* Animated Stars Rating */}
              <AnimatedStars score={correctCount} totalQuestions={customQuiz.length} />
              
              {/* Confetti for Perfect Score */}
              <ConfettiAnimation isActive={correctCount === customQuiz.length && customQuiz.length > 0} />
              
              {/* Motivational Message */}
              {(() => {
                const percentage = Math.round((correctCount / customQuiz.length) * 100);
                let message = "";
                let messageColor = "";
                
                if (percentage === 100) {
                  message = "🎉 Perfect Score! Outstanding work!";
                  messageColor = "text-green-600 dark:text-green-400";
                } else if (percentage >= 76) {
                  message = "🌟 Excellent job! You've mastered these words!";
                  messageColor = "text-green-600 dark:text-green-400";
                } else if (percentage >= 51) {
                  message = "👍 Good work! Keep practicing to improve!";
                  messageColor = "text-blue-600 dark:text-blue-400";
                } else if (percentage > 0) {
                  message = "💪 Keep trying! Practice makes perfect!";
                  messageColor = "text-orange-600 dark:text-orange-400";
                } else {
                  message = "📚 Don't give up! Review the words and try again!";
                  messageColor = "text-red-600 dark:text-red-400";
                }
                
                return (
                  <p className={`mt-2 text-sm font-medium ${messageColor}`}>
                    {message}
                  </p>
                );
              })()}
              
              <div className="mt-6 flex items-center gap-3 justify-center">
                <button
                  type="button"
                  onClick={() => setReviewMode(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Review Answers
                </button>
                <button
                  type="button"
                  onClick={handleRestart}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Restart Quiz
                </button>
                <button
                  type="button"
                  onClick={resetQuiz}
                  className="px-4 py-2 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-md hover:bg-green-200 dark:hover:bg-green-800"
                >
                  New Quiz
                </button>
              </div>
            </div>
          )}

          {reviewMode && (
            <div className="space-y-6 text-left">
              {customQuiz.map((q, i) => {
                const selected = selectedAnswers[i];
                const isCorrect = selected === q.answer;
                return (
                  <div key={i}>
                    <p className="font-medium text-gray-900 dark:text-white mb-2">{i + 1}. {q.question}</p>
                    <div className="flex flex-wrap gap-2">
                      {q.options.map(opt => {
                        const selectedThis = selected === opt;
                        const classes = [
                          'px-3 py-1 rounded-full border text-sm',
                          'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                          'text-gray-800 dark:text-white',
                        ];
                        if (selectedThis) classes.push(isCorrect ? 'bg-green-100 dark:bg-green-900 border-green-300 dark:border-green-700 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900 border-red-300 dark:border-red-700 text-red-800 dark:text-red-200');
                        if (!selectedThis && opt === q.answer) classes.push('ring-2 ring-green-400');
                        return (
                          <span key={opt} className={classes.join(' ')}>{opt}</span>
                        );
                      })}
                    </div>
                    {!selected && (
                      <p className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">You did not answer this question.</p>
                    )}
                    {selected && !isCorrect && (
                      <div className="mt-2 flex items-center justify-between">
                        <p className="text-sm text-red-700 dark:text-red-300">Correct answer: {q.answer}</p>
                        {q.sourceWordPairId && (
                          <button
                            onClick={() => setShowLearnPopup(q.sourceWordPairId)}
                            className="ml-3 px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors"
                          >
                            Learn
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setReviewMode(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Back to Results
                </button>
                <button
                  type="button"
                  onClick={handleRestart}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Restart Quiz
                </button>
                <button
                  type="button"
                  onClick={resetQuiz}
                  className="px-4 py-2 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-md hover:bg-green-200 dark:hover:bg-green-800"
                >
                  New Quiz
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Learn Popup */}
      {showLearnPopup && (
        <WordPairPopup
          wordPairId={showLearnPopup}
          onClose={() => setShowLearnPopup(null)}
          getLevelColor={getLevelColor}
          getLevelById={getLevelById}
          speakWord={speakWord}
          canSpeakWord={canSpeakWord}
          highlightWordInExample={highlightWordInExample}
          getPartOfSpeechColor={getPartOfSpeechColor}
        />
      )}
    </div>
  );
};

// TagsPage component moved outside to prevent re-creation on parent re-renders
const TagsPage = ({ tag: currentTag, setCurrentPage, loadWordData, getLevelColor, getLevelById, handleRouting }) => {
  const [filteredWords, setFilteredWords] = useState([]);
  const [tagInfo, setTagInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchAndFilter = async () => {
      setLoading(true);
      try {
        const [wordsRes, tagsRes] = await Promise.all([
          fetch('/indexes/words.json'),
          fetch('/indexes/tags.json')
        ]);
        const wordsJson = await wordsRes.json();
        const tagsJson = await tagsRes.json();
        const allWords = wordsJson.data || [];
        setFilteredWords(allWords.filter(word => word.tags && word.tags.includes(currentTag)));
        const foundTag = (tagsJson.data || []).find(t => t.id === currentTag);
        setTagInfo(foundTag || null);
      } catch (err) {
        setFilteredWords([]);
        setTagInfo(null);
      } finally {
        setLoading(false);
      }
    };
    fetchAndFilter();
  }, [currentTag]);

  // Set document title and meta tags when tagInfo is available
  useEffect(() => {
    if (tagInfo) {
      const documentTitle = tagInfo.title + ' | Commonly Confused Words';
      // Set document title
      document.title = documentTitle;
      
      // Helper function to update or create meta tag
      const updateMetaTag = (property, content) => {
        let meta = document.querySelector(`meta[property="${property}"]`) || 
                  document.querySelector(`meta[name="${property}"]`);
        if (meta) {
          meta.setAttribute('content', content);
        } else {
          meta = document.createElement('meta');
          if (property.startsWith('og:') || property.startsWith('twitter:')) {
            meta.setAttribute('property', property);
          } else {
            meta.setAttribute('name', property);
          }
          meta.setAttribute('content', content);
          document.head.appendChild(meta);
        }
      };
      
      // Set meta description
      updateMetaTag('description', tagInfo.description);
      
      // Set Open Graph meta tags
      updateMetaTag('og:title', documentTitle);
      updateMetaTag('og:description', tagInfo.description);
      updateMetaTag('og:type', 'website');
      updateMetaTag('og:url', window.location.href);
      
      // Set Twitter meta tags
      updateMetaTag('twitter:card', 'summary');
      updateMetaTag('twitter:title', documentTitle);
      updateMetaTag('twitter:description', tagInfo.description);
    }
  }, [tagInfo]);

  return (
    <div className="max-w-4xl mx-auto text-center">
      {!loading && (
        <>
          <h1 className="text-3xl md:text-4xl font-bold mb-2 text-gray-900 dark:text-white">
            {tagInfo ? tagInfo.title : currentTag.replace('-', ' ')}
          </h1>
          {tagInfo && tagInfo.description && (
            <p className="mb-6 text-gray-600 dark:text-gray-400">{tagInfo.description}</p>
          )}
        </>
      )}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        {loading && (
          <div className="col-span-2 text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading word pairs...</p>
          </div>
        )}
        {!loading && filteredWords.length === 0 && (
          <div className="col-span-2 text-gray-600 dark:text-gray-400">No word pairs found for this tag.</div>
        )}
        {!loading && filteredWords
          .sort((a, b) => (b.rank || 0) - (a.rank || 0)) // Sort by rank descending (highest first)
          .map((data) => (
          <div
            key={data.id}
            className="block p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 hover:scale-105"
          >
            {/* Row 1: Level (100% width, left-aligned) */}
            <div className="flex justify-start mb-4">
              <a
                href={`/levels/${data.level}/`}
                className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(getLevelById(data.level)?.code)} hover:underline cursor-pointer`}
              >
                {getLevelById(data.level)?.code} - {getLevelById(data.level)?.name}
              </a>
            </div>

            {/* Row 2: Word pair (100% width) */}
            <div className="mb-4">
              <a
                href={`/${data.id}/`}
                className="cursor-pointer"
                title={`The difference between ${data.title.replace(' vs. ', ' and ')}`}
                onClick={() => {
                  // Save current scroll position before navigating
                  sessionStorage.setItem('tagsScrollPosition', window.scrollY.toString());
                }}
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 hover:underline mb-2 transition-colors">
                  {data.title}
                </h3>
              </a>

            </div>

            {/* Row 3: Tags (100% width) */}
            <div className="flex flex-wrap gap-2">
              {data.tags && data.tags.map((tag) => (
                tag === currentTag ? (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-md text-xs"
                  >
                    {tag.replace('-', ' ')}
                  </span>
                ) : (
                  <a
                    key={tag}
                    href={`/tags/${tag}/`}
                    className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 rounded-md text-xs hover:underline cursor-pointer"
                  >
                    {tag.replace('-', ' ')}
                  </a>
                )
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// LevelsPage component moved outside to prevent re-creation on parent re-renders
const LevelsPage = ({ levelId, setCurrentPage, loadWordData, getLevelColor, getLevelById }) => {
  const [filteredWords, setFilteredWords] = useState([]);
  const [levelInfo, setLevelInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchAndFilter = async () => {
      setLoading(true);
      try {
        const [wordsRes, levelsRes] = await Promise.all([
          fetch('/indexes/words.json'),
          fetch('/indexes/levels.json')
        ]);
        const wordsJson = await wordsRes.json();
        const levelsJson = await levelsRes.json();
        const allWords = wordsJson.data || [];
        const filtered = allWords.filter(word => word.level === levelId);
        setFilteredWords(filtered);
        const foundLevel = (levelsJson.data || []).find(l => l.id === levelId);
        setLevelInfo(foundLevel || null);
      } catch (err) {
        setFilteredWords([]);
        setLevelInfo(null);
      } finally {
        setLoading(false);
      }
    };
    fetchAndFilter();
  }, [levelId]);

  // Set document title and meta tags when levelInfo is available
  useEffect(() => {
    if (levelInfo) {
      const documentTitle = levelInfo.title + ' | Commonly Confused Words';
      // Set document title
      document.title = documentTitle;
      
      // Helper function to update or create meta tag
      const updateMetaTag = (property, content) => {
        let meta = document.querySelector(`meta[property="${property}"]`) || 
                  document.querySelector(`meta[name="${property}"]`);
        if (meta) {
          meta.setAttribute('content', content);
        } else {
          meta = document.createElement('meta');
          if (property.startsWith('og:') || property.startsWith('twitter:')) {
            meta.setAttribute('property', property);
          } else {
            meta.setAttribute('name', property);
          }
          meta.setAttribute('content', content);
          document.head.appendChild(meta);
        }
      };
      
      // Set meta description
      updateMetaTag('description', levelInfo.description);
      
      // Set Open Graph meta tags
      updateMetaTag('og:title', documentTitle);
      updateMetaTag('og:description', levelInfo.description);
      updateMetaTag('og:type', 'website');
      updateMetaTag('og:url', window.location.href);
      
      // Set Twitter meta tags
      updateMetaTag('twitter:card', 'summary');
      updateMetaTag('twitter:title', documentTitle);
      updateMetaTag('twitter:description', levelInfo.description);
    }
  }, [levelInfo]);

  return (
    <div className="max-w-4xl mx-auto text-center">
      {!loading && (
        <>
          <h1 className="text-3xl md:text-4xl font-bold mb-2 text-gray-900 dark:text-white">
            {levelInfo ? levelInfo.title : levelId}
          </h1>
          {levelInfo && levelInfo.description && (
            <p className="mb-6 text-gray-600 dark:text-gray-400">{levelInfo.description}</p>
          )}
        </>
      )}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        {loading && (
          <div className="col-span-2 text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading word pairs...</p>
          </div>
        )}
        {!loading && filteredWords.length === 0 && (
          <div className="col-span-2 text-gray-600 dark:text-gray-400">No word pairs found for this level.</div>
        )}
        {!loading && filteredWords
          .sort((a, b) => (b.rank || 0) - (a.rank || 0)) // Sort by rank descending (highest first)
          .map((data) => (
          <div
            key={data.id}
            className="block p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 hover:scale-105"
          >
            {/* Row 1: Level (100% width, left-aligned, greyed out) */}
            <div className="flex justify-start mb-4">
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                {getLevelById(data.level)?.code} - {getLevelById(data.level)?.name}
              </span>
            </div>

            {/* Row 2: Word pair (100% width) */}
            <div className="mb-4">
              <a
                href={`/${data.id}/`}
                className="cursor-pointer"
                title={`The difference between ${data.title.replace(' vs. ', ' and ')}`}
                onClick={() => {
                  // Save current scroll position before navigating
                  sessionStorage.setItem('levelsScrollPosition', window.scrollY.toString());
                }}
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 hover:underline mb-2 transition-colors">
                  {data.title}
                </h3>
              </a>

            </div>

            {/* Row 3: Tags (100% width) */}
            <div className="flex flex-wrap gap-2">
              {data.tags && data.tags.map((tag) => (
                <a
                  key={tag}
                  href={`/tags/${tag}/`}
                  className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 rounded-md text-xs hover:underline cursor-pointer"
                >
                  {tag.replace('-', ' ')}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ConfusedWordsWebsite = () => {
  const [darkMode, setDarkMode] = useState(() => {
    // Initialize dark mode from localStorage immediately
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark';
    }
    return false;
  });
  const [currentPage, setCurrentPage] = useState(null); // Start as null to prevent flicker
  const [wordData, setWordData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [wordList, setWordList] = useState([]);
  const [levels, setLevels] = useState([]);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollYRef = useRef(0);

  // Load word data (simulate file reading)
  const loadWordData = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/data/words/${id}.json`);
      if (!response.ok) {
        throw new Error('Word pair not found');
      }
      const data = await response.json();
      setWordData(data);
      setCurrentPage(id);
    } catch (err) {
      setError('Failed to load word data. Please check if the file exists.');
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setWordData, setCurrentPage]);

  // Initialize page state from URL immediately (before wordList loads)
  const initializePageFromURL = useCallback(() => {
    let path = window.location.pathname;
    
    // Remove trailing slash if present (except for root)
    if (path !== '/' && path.endsWith('/')) {
      path = path.slice(0, -1);
    }
    
    // Handle GitHub Pages routing with query parameter fallback
    if (path === '/' && window.location.search.startsWith('?/')) {
      // This is a redirected GitHub Pages URL, extract the real path
      path = '/' + window.location.search.slice(2); // Remove '?/' and add leading slash
    }
    
    if (path === '/' || path === '' || path === '/confused-words') {
      setCurrentPage('home');
    } else if (path === '/quizzes') {
      setCurrentPage('quizzes');
      setWordData(null);
    } else if (path === '/flashcards') {
      setCurrentPage('flashcards');
      setWordData(null);
    } else if (path.startsWith('/levels/')) {
      const levelId = path.substring(8); // Remove '/levels/'
      setCurrentPage(`levels-${levelId}`);
      setWordData(null);
    } else if (path.startsWith('/tags/')) {
      const tag = path.substring(6); // Remove '/tags/'
      setCurrentPage(`tags-${tag}`);
      setWordData(null);
    } else {
      // For word pairs, we'll validate when wordList loads
      const idOrTagOrLevel = path.substring(1); // Remove leading slash
      setCurrentPage(idOrTagOrLevel); // Optimistically set, will validate later
    }
  }, []);

  // Handle URL routing (validates routes when wordList is available)
  const handleRouting = useCallback(() => {
    if (!wordList || wordList.length === 0) {
      // If wordList isn't loaded yet, just initialize from URL
      if (currentPage === null) {
        initializePageFromURL();
      }
      return;
    }
    
    let path = window.location.pathname;
    
    // Remove trailing slash if present (except for root)
    if (path !== '/' && path.endsWith('/')) {
      path = path.slice(0, -1);
    }
    
    // Handle GitHub Pages routing with query parameter fallback
    if (path === '/' && window.location.search.startsWith('?/')) {
      // This is a redirected GitHub Pages URL, extract the real path
      path = '/' + window.location.search.slice(2); // Remove '?/' and add leading slash
    }
    
    if (path === '/' || path === '' || path === '/confused-words') {
      setCurrentPage('home');
    } else if (path === '/quizzes') {
      setCurrentPage('quizzes');
      setWordData(null);
    } else if (path === '/flashcards') {
      setCurrentPage('flashcards');
      setWordData(null);
    } else {
      const idOrTagOrLevel = path.substring(1); // Remove leading slash
      const found = wordList.find(w => w.id === idOrTagOrLevel);
      if (found) {
        setCurrentPage(idOrTagOrLevel);
        loadWordData(idOrTagOrLevel);
      } else if (idOrTagOrLevel.startsWith('levels/')) {
        const levelId = idOrTagOrLevel.split('/')[1];
        const isLevel = wordList.some(w => w.level === levelId);
        if (isLevel) {
          setCurrentPage(`levels-${levelId}`);
          setWordData(null);
        } else {
          setCurrentPage('home');
          window.history.pushState(null, '', '/');
        }
      } else if (idOrTagOrLevel.startsWith('tags/')) {
        const tag = idOrTagOrLevel.split('/')[1];
        const isTag = wordList.some(w => w.tags && w.tags.includes(tag));
        if (isTag) {
          setCurrentPage(`tags-${tag}`);
          setWordData(null);
        } else {
          setCurrentPage('home');
          window.history.pushState(null, '', '/');
        }
      } else {
        setCurrentPage('home');
        window.history.pushState(null, '', '/');
      }
    }
  }, [wordList, loadWordData, setCurrentPage, setWordData, initializePageFromURL, currentPage]);

  // Initialize page from URL immediately on mount
  useEffect(() => {
    initializePageFromURL();
  }, [initializePageFromURL]);

  // Simple scroll position restoration for all pages
  useEffect(() => {
    // Check if we have a saved scroll position for the current page
    const pageKey = currentPage === 'home' ? 'home' : 
                   currentPage?.startsWith('levels-') ? 'levels' :
                   currentPage?.startsWith('tags-') ? 'tags' : null;
    
    if (pageKey) {
      const savedScroll = sessionStorage.getItem(`${pageKey}ScrollPosition`);
      if (savedScroll) {
        // Restore scroll position after a short delay to ensure page is rendered
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedScroll, 10));
          sessionStorage.removeItem(`${pageKey}ScrollPosition`); // Clean up after use
        }, 150);
      }
    }
  }, [currentPage]);

  useEffect(() => {
    handleRouting();
  }, [handleRouting]);

  // Trigger routing when wordList is loaded
  useEffect(() => {
    if (wordList && wordList.length > 0) {
      handleRouting();
    }
  }, [wordList, handleRouting]);

  // Hide header on scroll down, show on scroll up
  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY || window.pageYOffset;
      const lastY = lastScrollYRef.current;
      const delta = currentY - lastY;

      if (currentY < 10) {
        setIsHeaderVisible(true);
      } else if (Math.abs(delta) > 5) {
        if (delta > 0) {
          // Scrolling down
          setIsHeaderVisible(false);
        } else {
          // Scrolling up
          setIsHeaderVisible(true);
        }
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      handleRouting();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [handleRouting]); // Add handleRouting to dependency array

  // Fetch word list and levels from /indexes/words.json and /indexes/levels.json on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [wordsRes, levelsRes] = await Promise.all([
          fetch('/indexes/words.json'),
          fetch('/indexes/levels.json')
        ]);
        const wordsJson = await wordsRes.json();
        const levelsJson = await levelsRes.json();
        setWordList(wordsJson.data || []);
        setLevels(levelsJson.data || []);
      } catch (err) {
        setError('Failed to load word list or levels.');
      }
    };
    fetchData();
  }, []);

  // Theme is now initialized in useState, no need for useEffect

  // Apply dark mode class to document element
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    localStorage.setItem('theme', !darkMode ? 'dark' : 'light');
  };

  // Navigate to home
  const navigateToHome = () => {
    setCurrentPage('home');
    setWordData(null);
    window.history.pushState(null, '', '/');
  };

  // Check if word can be spoken (either MP3 exists or speech synthesis is supported)
  // Custom hook to check if audio file exists using fetch HEAD method
  const useCanSpeakWord = (word, mediaId) => {
    const [exists, setExists] = useState(true); // Default to true for better UX
    const [checked, setChecked] = useState(false);
    
    useEffect(() => {
      // Always return true if speech synthesis is supported
      if ('speechSynthesis' in window) {
        setExists(true);
        setChecked(true);
        return;
      }
      
      const checkFile = async () => {
        try {
          const filename = mediaId ? mediaId : word.toLowerCase();
          const audioPath = `/media/${filename}.mp3`;
          
          const response = await fetch(audioPath, { method: 'HEAD' });
          setExists(response.ok);
        } catch (error) {
          console.warn(`Error checking audio file existence for "${word}":`, error);
          setExists(false);
        } finally {
          setChecked(true);
        }
      };
      
      checkFile();
    }, [word, mediaId]);
    
    return { exists, checked };
  };

  // Audio playback function
  const speakWord = (word, mediaId) => {
    try {
      // Convert word to lowercase for filename
      const filename = mediaId ? mediaId : word.toLowerCase();
      const audioPath = `/media/${filename}.mp3`;
      
      // Create audio element and play
      const audio = new Audio(audioPath);
      audio.play().catch(error => {
        console.warn(`Could not play audio for "${word}":`, error);
        // Fallback to text-to-speech if MP3 file not found or fails to play
        if ('speechSynthesis' in window) {
          const utterance = new SpeechSynthesisUtterance(word);
          utterance.rate = 0.8;
          utterance.pitch = 1;
          speechSynthesis.speak(utterance);
        }
      });
    } catch (error) {
      console.warn(`Error playing audio for "${word}":`, error);
    }
  };

  // Highlight word in examples
  const highlightWordInExample = (example, word) => {
    return example
      .replaceAll('[', '<strong>')
      .replaceAll(']', '</strong>');
  };

  // Add getLevelColor function
  const getLevelColor = (levelCode) => {
    if (levelCode === 'A1' || levelCode === 'A2') return 'bg-[#FFF5C2]';
    if (levelCode === 'B1' || levelCode === 'B2') return 'bg-[#D9F8F2]';
    if (levelCode === 'C1' || levelCode === 'C2') return 'bg-[#FFDBE9]';
    return 'bg-gray-100 dark:bg-gray-700';
  };

  // Helper to get level object by id
  const getLevelById = (levelId) => levels.find(l => l.id === levelId);

  // Get ads enabled from environment variable
  const adsEnabled = process.env.REACT_APP_ENABLE_ADS === 'true';

  const HomePage = () => (
    <div className="max-w-4xl mx-auto text-center">
      <div className="mb-12">
        <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Words Unconfused
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
          Master the most commonly confused English word pairs with clear explanations and examples
        </p>
      </div>

      {adsEnabled && (
        <div className="my-6 flex justify-center">
          {/* Google AdSense Placeholder (Top) */}
          <div className="w-full max-w-md h-24 bg-gray-200 border border-gray-300 rounded flex items-center justify-center text-gray-500 text-sm">
            AdSense Placeholder (Top)
          </div>
        </div>
      )}
      
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        {wordList
          .sort((a, b) => (b.rank || 0) - (a.rank || 0)) // Sort by rank descending (highest first)
          .map((data) => (
          <div
            key={data.id}
            className="block p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 hover:scale-105"
          >
            {/* Row 1: Level (100% width, left-aligned) */}
            <div className="flex justify-start mb-4">
              <a
                href={`/levels/${data.level}/`}
                className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(getLevelById(data.level)?.code)} hover:underline cursor-pointer`}
              >
                {getLevelById(data.level)?.code} - {getLevelById(data.level)?.name}
              </a>
            </div>

            {/* Row 2: Word pair (100% width) */}
            <div className="mb-4">
              <a 
                href={`/${data.id}/`}
                className="cursor-pointer"
                title={`The difference between ${data.title.replace(' vs. ', ' and ')}`}
                onClick={() => {
                  // Save current scroll position before navigating
                  sessionStorage.setItem('homeScrollPosition', window.scrollY.toString());
                }}
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 hover:underline mb-2 transition-colors">
                  {data.title}
                </h3>
              </a>

            </div>

            {/* Row 3: Tags (100% width) */}
            <div className="flex flex-wrap gap-2">
              {data.tags && data.tags.map((tag) => (
                <a
                  key={tag}
                  href={`/tags/${tag}/`}
                  className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 rounded-md text-xs hover:underline cursor-pointer"
                >
                  {tag.replace('-', ' ')}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>

      {adsEnabled && (
        <div className="my-6 flex justify-center">
          {/* Google AdSense Placeholder (Bottom) */}
          <div className="w-full max-w-md h-24 bg-gray-200 border border-gray-300 rounded flex items-center justify-center text-gray-500 text-sm">
            AdSense Placeholder (Bottom)
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-8">
        <Lightbulb className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
          Why This Matters
        </h3>
        <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Using the right word at the right time makes your communication clearer and more professional. 
          These commonly confused pairs trip up even native speakers, so mastering them will set you apart.
        </p>
      </div>
    </div>
  );





  // Footer component
  const Footer = () => (
    <footer className="mt-16 py-8 border-t border-gray-200 dark:border-gray-700">
      <div className="max-w-4xl mx-auto text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-semibold text-gray-900 dark:text-white">Confused Words</span>
        </div>
 
        <div className="text-sm text-gray-500 dark:text-gray-400">
          © {new Date().getFullYear()} Confused Words. All rights reserved.
        </div>
      </div>
    </footer>
  );

  return (
    <div className="min-h-screen transition-colors duration-300 bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 pb-8">
        {/* Header */}
        <header className={`sticky top-0 z-50 flex justify-between items-center mb-12 ${darkMode ? 'bg-gray-900' : 'bg-white'} transition-opacity duration-300 py-3 px-4 ${isHeaderVisible ? 'opacity-100 shadow-sm' : 'opacity-0 shadow-none'}`}>
          <div 
            onClick={navigateToHome}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                setCurrentPage('quizzes');
                setWordData(null);
                window.history.pushState(null, '', '/quizzes');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors font-medium"
            >
              <Brain className="w-4 h-4" />
              Quizzes
            </button>
            
            <button
              onClick={() => {
                setCurrentPage('flashcards');
                setWordData(null);
                window.history.pushState(null, '', '/flashcards');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-800 transition-colors font-medium"
            >
              <GraduationCap className="w-4 h-4" />
              Flashcards
            </button>
          
          <button
            onClick={toggleDarkMode}
            className="p-3 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
          >
            {darkMode ? (
              <Sun className="w-5 h-5 text-yellow-500" />
            ) : (
              <Moon className="w-5 h-5 text-gray-600" />
            )}
          </button>
          </div>
        </header>

        {/* Main Content */}
        <main>
          {loading && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading word data...</p>
            </div>
          )}
          
          {error && (
            <div className="text-center py-12">
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={navigateToHome}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                Return to Home
              </button>
            </div>
          )}
          
          {currentPage === null && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Loading...</p>
            </div>
          )}
          {!loading && !error && currentPage === 'home' && <HomePage />}
          {!loading && !error && currentPage === 'quizzes' && <QuizzesPage levels={levels} getLevelColor={getLevelColor} speakWord={speakWord} highlightWordInExample={highlightWordInExample} getPartOfSpeechColor={getPartOfSpeechColor} getLevelById={getLevelById} />}
          {!loading && !error && currentPage === 'flashcards' && <FlashcardsPage levels={levels} getLevelColor={getLevelColor} speakWord={speakWord} highlightWordInExample={highlightWordInExample} getPartOfSpeechColor={getPartOfSpeechColor} getLevelById={getLevelById} />}
          {!loading && !error && currentPage && currentPage !== 'home' && currentPage !== 'quizzes' && currentPage !== 'flashcards' && wordData && (
            <WordsPage data={wordData} adsEnabled={adsEnabled} getLevelColor={getLevelColor} getLevelById={getLevelById} speakWord={speakWord} useCanSpeakWord={useCanSpeakWord} highlightWordInExample={highlightWordInExample} getPartOfSpeechColor={getPartOfSpeechColor} loadWordData={loadWordData} setCurrentPage={setCurrentPage} />
          )}
          {!loading && !error && currentPage && currentPage.startsWith('tags-') && (
            <TagsPage 
              tag={currentPage.replace('tags-', '')} 
              setCurrentPage={setCurrentPage}
              loadWordData={loadWordData}
              getLevelColor={getLevelColor}
              getLevelById={getLevelById}
              handleRouting={handleRouting}
            />
          )}
          {!loading && !error && currentPage && currentPage.startsWith('levels-') && (
            <LevelsPage 
              levelId={currentPage.replace('levels-', '')} 
              setCurrentPage={setCurrentPage}
              loadWordData={loadWordData}
              getLevelColor={getLevelColor}
              getLevelById={getLevelById}
            />
          )}
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default ConfusedWordsWebsite;